import { useWorkspace } from "../../context/WorkspaceContext";
import {
  ResourceFeatures,
  ResourceFeaturesHandle,
} from "../VehicleFeatures/ResourceFeatures";
/* eslint-disable jsx-a11y/anchor-is-valid */
import { VehicleDriverAccess } from "./VehicleDriverAccess";
import type { FC } from "react";
import { useMemo, useRef, useState } from "react";
import { Label, TextInput, Textarea, Select } from "flowbite-react";
import { HiChevronDown, HiOutlinePlus, HiOutlineSearch } from "react-icons/hi";
import { StandardForm, EntityFormDialog, Button } from "..";
import { IVehicle, IParameter, ICompany, ParameterType } from "../../types";
import { useTranslation } from "react-i18next";
import { Vehicle } from "../../models";
import { getApiToken } from "../../data/api/token";
import { lookupVehicle } from "../../data/api/vehicles";

export interface VehicleModalProp {
  data: IVehicle;
  companies: ICompany[];
  cateogries: IParameter[];
  isShowing: boolean;
  onConfirm: (data: IVehicle) => void | Promise<void>;
  onCancel: () => void;
  workspaceId?: string;
  saveError?: string | null;
}

const normalizeRegistrationNumber = (value: string) =>
  value.replace(/[\s.-]/g, "").toUpperCase();

const clearRegistryDetails = (vehicle: IVehicle): IVehicle => ({
  ...vehicle,
  maxWeight: 3500,
  vehicleMake: null,
  vehicleModel: null,
  vehicleTypeDesignation: null,
  vin: null,
  curbWeightKg: null,
  payloadCapacityKg: null,
  permittedTotalWeightKg: null,
  lengthCm: null,
  widthCm: null,
  heightCm: null,
  fuelTypes: [],
  co2EmissionsGKm: null,
  co2EmissionsSource: null,
  co2MeasurementMethod: null,
  fuelConsumptionLPer100Km: null,
  electricEnergyConsumptionWhPerKm: null,
  electricRangeKm: null,
  euroClass: null,
  nextInspectionDate: null,
  vehicleStatus: null,
  registrySource: null,
  registryFetchedAt: undefined,
});

export const VehicleForm: FC<VehicleModalProp> = function (
  props: VehicleModalProp,
) {
  const { activeWorkspace } = useWorkspace();
  const attributes = useRef<ResourceFeaturesHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const defaultCompanyId =
    props.data.companyId.length > 0
      ? props.data.companyId
      : props.companies[0]?.id;

  const { t } = useTranslation(["common", "inventory", "company"]);

  const [isNew] = useState(Vehicle.isNew(props.data));
  const [formVehicle, setFormVehicle] = useState<IVehicle>({ ...props.data });
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupComplete, setLookupComplete] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const selectableCategories = useMemo(
    () =>
      props.cateogries.length > 0
        ? props.cateogries
        : [
            {
              id: "Standard",
              workspaceId: props.data.workspaceId ?? "",
              code: "Standard",
              type: ParameterType.VehicleType,
            },
          ],
    [props.cateogries, props.data.workspaceId],
  );
  const defaultCategoryCode =
    props.data.categoryCode ??
    (isNew
      ? (selectableCategories.find(
          (category) => category.code.trim().toLowerCase() === "standard",
        )?.code ?? selectableCategories[0]?.code)
      : "");

  const updateField = (field: keyof IVehicle, value: unknown) => {
    setFormVehicle((current) => ({ ...current, [field]: value }));
  };

  const updateRegistrationNumber = (value: string) => {
    const registrationNumber = value.toUpperCase();
    setFormVehicle((current) => {
      const registrationChanged =
        normalizeRegistrationNumber(registrationNumber) !==
        normalizeRegistrationNumber(current.registrationNumber ?? "");
      const next =
        registrationChanged && current.registrySource
          ? clearRegistryDetails(current)
          : current;
      return { ...next, registrationNumber };
    });
    setLookupComplete(false);
    setLookupError(null);
  };

  const handleLookup = async () => {
    const registrationNumber = formVehicle.registrationNumber?.trim();
    if (!registrationNumber || !props.workspaceId) {
      setLookupError(t("inventory:vehicle_lookup_plate_required"));
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    try {
      const token = await getApiToken();
      const registry = await lookupVehicle(
        token,
        props.workspaceId,
        registrationNumber,
      );
      setFormVehicle((current) => ({
        ...current,
        ...registry,
        maxWeight: registry.permittedTotalWeightKg ?? current.maxWeight ?? 3500,
      }));
      setLookupComplete(true);
      setDetailsOpen(true);
    } catch (error: any) {
      setLookupError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          t("inventory:vehicle_lookup_failed"),
      );
    } finally {
      setIsLookingUp(false);
    }
  };

  const numericValue = (value?: number | null) => value ?? "";
  const numericInputClass =
    "[&_input]:[appearance:textfield] [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none";
  const registryVehicleName = [
    formVehicle.vehicleMake,
    formVehicle.vehicleModel,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <StandardForm
      showHeader={false}
      description={t("modals:descriptions.new_vehicle")}
      fields={[
        {
          id: "companyId",
          label: t("company:select_company"),
          input: (
            <Select
              key={`vehicle-company-${defaultCompanyId}-${props.companies
                .map((company) => company.id)
                .join("-")}`}
              id="companyId"
              name="companyId"
              defaultValue={defaultCompanyId}
            >
              {props.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          ),
        },
        {
          id: "categoryCode",
          label: t("inventory:vehicle_type"),
          input: (
            <Select
              key={`vehicle-category-${selectableCategories
                .map((category) => category.code)
                .join("-")}-${defaultCategoryCode}`}
              id="categoryCode"
              name="categoryCode"
              defaultValue={defaultCategoryCode}
            >
              {selectableCategories.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.code}
                </option>
              ))}
            </Select>
          ),
        },
        {
          id: "registrationNumber",
          label: t("inventory:registration_number"),
          tooltip: t("inventory:vehicle_registration_lookup_tooltip"),
          grid_style: "col-span-2",
          input: (
            <div>
              <div className="flex items-stretch gap-2">
                <TextInput
                  id="registrationNumber"
                  key="registrationNumber"
                  name="registrationNumber"
                  placeholder={t("inventory:registration_number_description")}
                  value={formVehicle.registrationNumber}
                  onChange={(event) =>
                    updateRegistrationNumber(event.target.value)
                  }
                  type="text"
                  className="min-w-0 flex-1"
                  required={true}
                  disabled={isLookingUp}
                />
                <Button
                  type="button"
                  variant="primary"
                  icon={
                    <HiOutlineSearch
                      className={`h-5 w-5 ${isLookingUp ? "animate-pulse" : ""}`}
                    />
                  }
                  iconPosition="only"
                  className="!h-[42px] !w-[42px] !border-transparent !bg-brand !px-0 !text-white hover:!bg-brand-hover"
                  aria-label={t("inventory:vehicle_lookup_button")}
                  title={t("inventory:vehicle_lookup_button")}
                  onClick={handleLookup}
                  disabled={isLookingUp}
                />
              </div>
              {lookupComplete && !lookupError ? (
                <div className="mt-2">
                  {registryVehicleName ? (
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {registryVehicleName}
                    </p>
                  ) : null}
                  <p className="text-xs text-green-700">
                    {t("inventory:vehicle_lookup_success")}
                  </p>
                </div>
              ) : null}
              {lookupError ? (
                <p className="mt-2 text-xs text-red-600">{lookupError}</p>
              ) : null}
            </div>
          ),
        },
        {
          id: "name",
          label: t("inventory:description"),
          grid_style: "col-span-2",
          input: (
            <Textarea
              id="name"
              name="name"
              placeholder={t("inventory:vehicle_description")}
              value={formVehicle.name}
              onChange={(event) => updateField("name", event.target.value)}
              className="text-sm"
            />
          ),
        },
        ...(props.data.id
          ? [
              {
                id: "vehicleFeatures",
                grid_style: "col-span-2",
                input: (
                  <ResourceFeatures
                    key={props.data.id}
                    workspaceId={props.workspaceId || props.data.workspaceId}
                    resourceId={props.data.id}
                    kind="vehicle"
                    ref={attributes}
                    embedded
                    disabled={isSaving}
                    readOnly={
                      !["Owner", "Administrator"].includes(activeWorkspace?.role || "")
                    }
                  />
                ),
              },
              {
                id: "driverAccess",
                grid_style: "col-span-2",
                input: (
                  <VehicleDriverAccess
                    key={props.data.id}
                    workspaceId={props.workspaceId || props.data.workspaceId}
                    vehicleId={props.data.id}
                  />
                ),
              },
            ]
          : []),
        {
          id: "vehicleDetails",
          grid_style: "col-span-2",
          input: (
            <details
              open={detailsOpen}
              onToggle={(event) => setDetailsOpen(event.currentTarget.open)}
            >
              <summary className="inline-flex cursor-pointer list-none items-center gap-1 py-1 text-sm font-medium text-brand hover:underline [&::-webkit-details-marker]:hidden">
                {detailsOpen
                  ? t("inventory:vehicle_details_hide")
                  : t("inventory:vehicle_details_show")}
                <HiChevronDown
                  className={`h-4 w-4 transition-transform ${
                    detailsOpen ? "rotate-180" : ""
                  }`}
                />
              </summary>
              <div
                className="grid grid-cols-2 gap-4 pt-4"
                onInvalidCapture={(event) => {
                  // Native validation cannot focus a control inside a closed
                  // <details>. Open it synchronously before the browser focuses
                  // the invalid field, then keep React state in sync.
                  const details = event.currentTarget.closest("details");
                  if (details) details.open = true;
                  setDetailsOpen(true);
                }}
              >
                <div>
                  <Label className="mb-2 block" htmlFor="maxWeight">
                    {t("inventory:vehicle_max_weight")}
                  </Label>
                  <TextInput
                    id="maxWeight"
                    name="maxWeight"
                    placeholder={t("inventory:vehicle_max_weight_description")}
                    value={numericValue(formVehicle.maxWeight)}
                    onChange={(event) =>
                      updateField(
                        "maxWeight",
                        event.target.value === ""
                          ? null
                          : Number(event.target.value),
                      )
                    }
                    type="number"
                    className={numericInputClass}
                    required={true}
                  />
                </div>
                <div>
                  <Label className="mb-2 block" htmlFor="vehicleMake">
                    {t("inventory:vehicle_make")}
                  </Label>
                  <TextInput
                    id="vehicleMake"
                    name="vehicleMake"
                    value={formVehicle.vehicleMake ?? ""}
                    onChange={(event) =>
                      updateField("vehicleMake", event.target.value)
                    }
                  />
                </div>
                <div>
                  <Label className="mb-2 block" htmlFor="vehicleModel">
                    {t("inventory:vehicle_model")}
                  </Label>
                  <TextInput
                    id="vehicleModel"
                    name="vehicleModel"
                    value={formVehicle.vehicleModel ?? ""}
                    onChange={(event) =>
                      updateField("vehicleModel", event.target.value)
                    }
                  />
                </div>
                {(
                  [
                    [
                      "vehicleTypeDesignation",
                      t("inventory:vehicle_type_designation"),
                      formVehicle.vehicleTypeDesignation,
                    ],
                    ["vin", t("inventory:vehicle_vin"), formVehicle.vin],
                    [
                      "curbWeightKg",
                      t("inventory:vehicle_curb_weight"),
                      formVehicle.curbWeightKg,
                    ],
                    [
                      "payloadCapacityKg",
                      t("inventory:vehicle_payload"),
                      formVehicle.payloadCapacityKg,
                    ],
                    [
                      "lengthCm",
                      t("inventory:vehicle_length"),
                      formVehicle.lengthCm,
                    ],
                    [
                      "widthCm",
                      t("inventory:vehicle_width"),
                      formVehicle.widthCm,
                    ],
                    [
                      "fuelTypes",
                      t("inventory:vehicle_fuel_types"),
                      formVehicle.fuelTypes?.join(", "),
                    ],
                    [
                      "co2EmissionsGKm",
                      t("inventory:vehicle_co2"),
                      formVehicle.co2EmissionsGKm,
                    ],
                    [
                      "co2MeasurementMethod",
                      t("inventory:vehicle_co2_method"),
                      formVehicle.co2MeasurementMethod,
                    ],
                    [
                      "euroClass",
                      t("inventory:vehicle_euro_class"),
                      formVehicle.euroClass,
                    ],
                    [
                      "nextInspectionDate",
                      t("inventory:vehicle_next_inspection"),
                      formVehicle.nextInspectionDate,
                    ],
                    [
                      "vehicleStatus",
                      t("inventory:vehicle_status"),
                      formVehicle.vehicleStatus,
                    ],
                  ] as Array<[string, string, unknown]>
                ).map(([id, label, value]) => (
                  <div key={id}>
                    <Label className="mb-2 block" htmlFor={id}>
                      {label}
                    </Label>
                    <TextInput
                      id={id}
                      name={id}
                      value={value == null ? "" : String(value)}
                      readOnly
                    />
                  </div>
                ))}
                {(
                  [
                    [
                      "fuelConsumptionLPer100Km",
                      t("inventory:vehicle_fuel_consumption"),
                      formVehicle.fuelConsumptionLPer100Km,
                      "0.1",
                    ],
                    [
                      "electricEnergyConsumptionWhPerKm",
                      t("inventory:vehicle_electric_consumption"),
                      formVehicle.electricEnergyConsumptionWhPerKm,
                      "1",
                    ],
                    [
                      "electricRangeKm",
                      t("inventory:vehicle_electric_range"),
                      formVehicle.electricRangeKm,
                      "1",
                    ],
                  ] as Array<
                    [keyof IVehicle, string, number | null | undefined, string]
                  >
                ).map(([id, label, value, step]) => (
                  <div key={id}>
                    <Label className="mb-2 block" htmlFor={id}>
                      {label}
                    </Label>
                    <TextInput
                      id={id}
                      name={id}
                      type="number"
                      min={0}
                      step={step}
                      className={numericInputClass}
                      value={numericValue(value)}
                      onChange={(event) =>
                        updateField(
                          id,
                          event.target.value === ""
                            ? null
                            : Number(event.target.value),
                        )
                      }
                    />
                  </div>
                ))}
                <div>
                  <Label className="mb-2 block" htmlFor="heightCm">
                    {t("inventory:vehicle_height")}
                  </Label>
                  <TextInput
                    id="heightCm"
                    name="heightCm"
                    type="number"
                    className={numericInputClass}
                    value={numericValue(formVehicle.heightCm)}
                    onChange={(event) =>
                      updateField(
                        "heightCm",
                        event.target.value === ""
                          ? null
                          : Number(event.target.value),
                      )
                    }
                  />
                </div>
                <div>
                  <Label className="mb-2 block" htmlFor="palletSpaces">
                    {t("inventory:vehicle_pallet_spaces")}
                  </Label>
                  <TextInput
                    id="palletSpaces"
                    name="palletSpaces"
                    type="number"
                    min={0}
                    required={false}
                    className={numericInputClass}
                    value={numericValue(formVehicle.palletSpaces)}
                    onChange={(event) =>
                      updateField(
                        "palletSpaces",
                        event.target.value === ""
                          ? null
                          : Number(event.target.value),
                      )
                    }
                  />
                </div>
                <div>
                  <Label className="mb-2 block" htmlFor="cargoVolumeM3">
                    {t("inventory:vehicle_cargo_volume")}
                  </Label>
                  <TextInput
                    id="cargoVolumeM3"
                    name="cargoVolumeM3"
                    type="number"
                    min={0}
                    step="0.1"
                    required={false}
                    className={numericInputClass}
                    value={numericValue(formVehicle.cargoVolumeM3)}
                    onChange={(event) =>
                      updateField(
                        "cargoVolumeM3",
                        event.target.value === ""
                          ? null
                          : Number(event.target.value),
                      )
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Label className="mb-2 block" htmlFor="specialEquipment">
                    {t("inventory:vehicle_special_equipment")}
                  </Label>
                  <TextInput
                    id="specialEquipment"
                    name="specialEquipment"
                    placeholder={t(
                      "inventory:vehicle_special_equipment_description",
                    )}
                    value={formVehicle.specialEquipment?.join(", ") ?? ""}
                    onChange={(event) =>
                      updateField(
                        "specialEquipment",
                        event.target.value
                          .split(",")
                          .map((item) => item.trim()),
                      )
                    }
                  />
                </div>
              </div>
            </details>
          ),
        },
      ]}
      isSubmitting={isSaving}
      onConfirm={async (data) => {
        if (isSaving) return;
        setIsSaving(true);
        try {
          await attributes.current?.save();
          await props.onConfirm({
            ...formVehicle,
            companyId: data.companyId,
            categoryCode: data.categoryCode,
            maxWeight: Number(formVehicle.maxWeight),
            permittedTotalWeightKg: Number(formVehicle.maxWeight),
            heightCm: formVehicle.heightCm,
            palletSpaces: formVehicle.palletSpaces,
            cargoVolumeM3: formVehicle.cargoVolumeM3,
            specialEquipment: formVehicle.specialEquipment?.filter(Boolean) ?? [],
          });
        } catch {
          // Attribute conflicts are shown inline; keep the complete form open.
        } finally {
          setIsSaving(false);
        }
      }}
      onCancel={props.onCancel}
      buttonConfirmText={t("modals:button_texts.vehicle")}
      buttonConfirmPrependIcon={
        isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null
      }
      buttonCloseText={t("common:close")}
    />
  );
};

export const VehicleModal: FC<VehicleModalProp> = function (
  props: VehicleModalProp,
) {
  const { t } = useTranslation(["common", "inventory", "modals"]);

  return (
    <EntityFormDialog
      title={
        Vehicle.isNew(props.data)
          ? t("inventory:create_vehicle")
          : t("inventory:edit_vehicle")
      }
      show={props.isShowing}
      onCancel={props.onCancel}
    >
      {props.saveError ? (
        <p className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">
          {props.saveError}
        </p>
      ) : null}
      <VehicleForm {...props} />
    </EntityFormDialog>
  );
};
